import { css, keyframes } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Outlet from '../../assets/images/balloon_outlet.svg';
import { ReactComponent as Balloon } from '../../assets/images/btn_decision.svg';
import { ReactComponent as ActiveCellBackground } from '../../assets/images/reaction/img_reaction_back.svg';
import { FadeInImage } from '../shared/FadeInImage';

interface ReactionCellProps {
  name: string;
  src: string;
  onClick: () => void;
  isSelected?: boolean;
  isSending?: boolean;
}

const hover = keyframes`
  0% {
    transform: scale(1)
  }
  50% {
    transform: scale(1.2)
  }
  100% {
    transform: scale(1)
  }
`;

export const ReactionCell: React.FC<ReactionCellProps> = ({
  name,
  src,
  onClick,
  isSelected,
  isSending
}) => {
  const { t } = useTranslation('reaction');
  return (
    <li
      className={css`
        display: inline-block;
        position: relative;
        width: 25%;
        padding: 8px 4px 12px;
        .cell {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          padding: 0;
        }
        .cell:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .selected:disabled {
          opacity: 1;
        }
        .cell .image {
          position: relative;
          width: 56px;
          height: 56px;
          margin: auto;
          transition: transform 0.15s cubic-bezier(0.17, 0.89, 0.32, 1.49);
        }
        .cell:active > .image {
          transform: scale(0.88);
        }
        .name {
          width: 100%;
          font-size: 10px;
          white-space: nowrap;
          text-overflow: ellipsis;
          text-align: center;
          color: #897a66;
          overflow: hidden;
          padding: 6px 0px 0;
          opacity: ${isSelected ? 0 : 1};
          transition: 0.1s opacity linear;
        }
        .selected > .image > img {
          transform: scale(1.1);
        }
        .image > svg {
          display: none;
        }
        .selected > .image > svg {
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          transform: scale(1.2);
          @media (prefers-reduced-motion: no-preference) {
            transform: scale(1);
            animation: 1s ${hover} ease infinite;
          }
        }
        .balloon {
          display: inline-block;
          visibility: ${isSelected ? 'visible' : 'hidden'};
          opacity: ${isSelected ? 0.8 : 0};
          text-align: center;
          position: absolute;
          min-width: 100px;
          word-break: keep-all;
          white-space: nowrap;
          font-size: 14px;
          color: #ffffff;
          top: -16px;
          z-index: 2;
          padding: 2px 12px;
          border-radius: 28px;
          /* workaround for iOS Safari 12 */
          transform: translate3d(0, 0, 0);
          transition: 0.3s opacity linear;

          svg {
            position: absolute;
            color: #14c5b6;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
          }
          span {
            position: relative;
          }
        }
        .balloon::before {
          content: '';
          width: 15px;
          height: 6px;
          background-image: url(${Outlet});
          background-repeat: no-repeat;
          position: absolute;
          bottom: -5px;
        }
        :nth-child(4n) > .balloon {
          right: 0;
          &::before {
            left: auto;
            right: 26px;
          }
        }
        :nth-child(4n + 1) > .balloon {
          left: 0;
          &::before {
            left: 26px;
            right: auto;
          }
        }
        :nth-child(4n + 2) > .balloon {
          left: 0;
          &::before {
            left: 26px;
            right: auto;
          }
        }
        :nth-child(4n + 3) > .balloon {
          right: 0;
          &::before {
            left: auto;
            right: 26px;
          }
        }
        .hide {
          display: none;
        }
        @media (min-width: 360px) {
          width: 20%;
          :nth-child(5n) > .balloon {
            right: 0;
            left: auto;
            &::before {
              left: auto;
              right: 30px;
            }
          }
          :nth-child(5n + 1) > .balloon {
            left: 0;
            right: auto;
            &::before {
              left: 30px;
              right: auto;
            }
          }
          :nth-child(5n + 2) > .balloon {
            left: 0;
            right: auto;
            &::before {
              left: 30px;
              right: auto;
            }
          }
          :nth-child(5n + 3) > .balloon {
            left: 0;
            right: auto;
            &::before {
              left: 30px;
              right: auto;
            }
          }
          :nth-child(5n + 4) > .balloon {
            right: 0;
            left: auto;
            &::before {
              left: auto;
              right: 30px;
            }
          }
        }
        @media (min-width: 415px) {
          padding: 8px 6px 12px;
          .cell .image {
            width: 64px;
            height: 64px;
          }
        }
      `}
    >
      <button
        className={`cell ${isSelected ? 'selected' : ''}`}
        onClick={onClick}
        disabled={isSending}
        aria-describedby={isSelected ? 'cell-desc' : undefined}
      >
        <div className="image">
          <ActiveCellBackground />
          <FadeInImage
            src={src}
            style={{
              margin: 'auto',
              position: 'relative',
              zIndex: 1,
              transition: '0.4s transform cubic-bezier(0.46, 1.5, 0.64, 1.5) , opacity 0.2s ease 0s'
            }}
          />
        </div>
        <div className="name">{name}</div>
      </button>

      <div className="balloon" aria-hidden={true}>
        <Balloon />
        <span>{name}</span>
      </div>
      {isSelected && (
        <div id="cell-desc" className="hide">
          {t('aria_cell_desc')}
        </div>
      )}
    </li>
  );
};
